import { useState } from "react";

import { DISPLAYABLE_IMAGE_TYPES, MAX_MESSAGE_ATTACHMENTS } from "../../shared/support-schemas";
import { uploadSupportFile } from "./support-api";

// ADR-0089: files on a reply or note. Each file uploads as soon as it is dropped, pasted, or
// picked, so sending only names files that are already stored.

/** The largest file the desk accepts; the server enforces the same limit. */
const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Files on a reply are emailed, and an email can carry at most this much. */
export const MAX_REPLY_FILE_BYTES = 3_500_000;

export type ComposerFile = {
  key: string;
  filename: string;
  byteSize: number;
  image: boolean;
  status: "uploading" | "ready" | "failed";
  uploadId: string | null;
  problem: string | null;
  /** A local `blob:` URL that shows an image before it is sent. */
  preview: string | null;
};

const previewOf = (file: File) =>
  DISPLAYABLE_IMAGE_TYPES.has(file.type) && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(file)
    : null;

const release = (files: readonly ComposerFile[]) => {
  for (const file of files) if (file.preview) URL.revokeObjectURL(file.preview);
};

export function useComposerFiles(ticketId: string) {
  const [files, setFiles] = useState<readonly ComposerFile[]>([]);
  const update = (key: string, change: Partial<ComposerFile>) => {
    setFiles((current) =>
      current.map((file) => (file.key === key ? { ...file, ...change } : file)),
    );
  };
  const add = (incoming: readonly File[]) => {
    const room = Math.max(0, MAX_MESSAGE_ATTACHMENTS - files.length);
    const added = incoming.slice(0, room).map((file) => {
      const entry: ComposerFile = {
        key: crypto.randomUUID(),
        filename: file.name.trim() || "pasted-image.png",
        byteSize: file.size,
        image: file.type.startsWith("image/"),
        status: file.size > MAX_FILE_BYTES ? "failed" : "uploading",
        uploadId: null,
        problem: file.size > MAX_FILE_BYTES ? "Over 10 MB" : null,
        preview: previewOf(file),
      };
      if (entry.status === "uploading")
        void uploadSupportFile(ticketId, file).then(
          (result) => {
            update(entry.key, { status: "ready", uploadId: result.upload.id });
          },
          (error: unknown) => {
            update(entry.key, {
              status: "failed",
              problem: error instanceof Error ? error.message : "Upload failed",
            });
          },
        );
      return entry;
    });
    setFiles((current) => [...current, ...added]);
  };
  const remove = (key: string) => {
    release(files.filter((file) => file.key === key));
    setFiles((current) => current.filter((file) => file.key !== key));
  };
  const ready = files.filter((file) => file.status === "ready");
  return {
    files,
    add,
    remove,
    clear: () => {
      release(files);
      setFiles([]);
    },
    uploadIds: ready.map((file) => file.uploadId).filter((id): id is string => id !== null),
    uploading: files.some((file) => file.status === "uploading"),
    failed: files.some((file) => file.status === "failed"),
    readyBytes: ready.reduce((sum, file) => sum + file.byteSize, 0),
    full: files.length >= MAX_MESSAGE_ATTACHMENTS,
  };
}
